import React from "react";
import { useState } from "react";
import { message, Spin, Modal } from "antd";
import { StatsModal } from "./StatsModal";
import { GiveawayCard } from "./GiveawayCard";
import { GiveawayForm } from "./GiveawayForm";
import useContestService from "../../../../services/contestService";
import { useLanguage } from "../../../../context/LanguageContext";
import {
  giveAwayContent,
  replaceTemplate,
  getStatusText,
  getBulkActionText,
} from "../../../../utils/giveaAwayContent";

const { confirm } = Modal;

// components/giveaways/GiveawayTab.jsx
export function GiveawayTab({
  giveaways,
  setGiveaways,
  setActiveTab,
  loading = false,
  pagination,
  filters,
  onFilterChange,
  onPageChange,
  onRefresh,
  onDrawWinners,
  contestService,
}) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingGiveaway, setEditingGiveaway] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState(filters?.status || "");
  const [statsModal, setStatsModal] = useState({
    open: false,
    giveaway: null,
    stats: null,
    loading: false,
  });
  const { language } = useLanguage();

  const content = giveAwayContent[language].giveawayTab;

  // ✅ VERWENDE DEN IMPORTIERTEN SERVICE KOMPLETT
  const {
    updateContest,
    createContest,
    deleteContest,
    getContestStats,
    drawWinners,
    getContestWinners,
    loading: serviceLoading,
    error: serviceError,
  } = useContestService();

  // ===== COMPUTED VALUES =====
  const giveawayCounts = {
    all: giveaways.length,
    upcoming: giveaways.filter((g) => g.status === "upcoming").length,
    active: giveaways.filter((g) => g.status === "active").length,
    ended: giveaways.filter((g) => g.status === "ended").length,
    drawn: giveaways.filter((g) => g.status === "drawn").length,
  };

  // Komplett ersetzen:
  const showDeleteConfirm = (giveaway, onConfirm) => {
    const isHardDelete = giveaway?.status === "upcoming";

    confirm({
      title: isHardDelete
        ? content.deleteModal?.title || "Gewinnspiel löschen?"
        : content.deleteModal?.archiveTitle || "Gewinnspiel archivieren?",
      icon: (
        <span
          style={{
            color: isHardDelete ? "#ff4d4f" : "#faad14",
            fontSize: "22px",
          }}
        >
          {isHardDelete ? "🗑️" : "📦"}
        </span>
      ),
      content: (
        <div>
          <p>
            {isHardDelete
              ? content.deleteModal?.content ||
                "Dieses Gewinnspiel wird dauerhaft gelöscht."
              : content.deleteModal?.archiveContent ||
                "Dieses Gewinnspiel wird archiviert und aus der Liste ausgeblendet."}
          </p>

          <div className="mt-3 p-3 bg-gray-50 rounded">
            <strong>
              {giveaway?.title ||
                content.deleteModal?.unknownGiveaway ||
                "Unbekanntes Gewinnspiel"}
            </strong>
            {giveaway?.description && (
              <p className="text-sm text-gray-600 mt-1">
                {giveaway.description}
              </p>
            )}
          </div>

          <p className="mt-3 text-red-600 text-sm">
            {isHardDelete
              ? content.deleteModal?.warning ||
                "Achtung: Diese Aktion kann nicht rückgängig gemacht werden."
              : content.deleteModal?.archiveWarning ||
                "Hinweis: Archivierte Gewinnspiele können nicht mehr ausgelost werden."}
          </p>
        </div>
      ),
      okText: isHardDelete
        ? content.deleteModal?.okText || "Ja, löschen"
        : content.deleteModal?.archiveOkText || "Ja, archivieren",
      okType: isHardDelete ? "danger" : "primary",
      cancelText: content.deleteModal?.cancelText || "Abbrechen",
      onOk: onConfirm,
      width: 550,
    });
  };

  const showBulkActionConfirm = (action, selectedGiveaways, onConfirm) => {
    const actionConfig = getBulkActionText(action, language);

    if (!actionConfig) {
      message.warning(content.error?.unknownAction || "Unknown action");
      return;
    }

    // ✅ Fallback für confirmText
    const confirmText =
      language === "de"
        ? `Sind Sie sicher, dass Sie ${
            selectedGiveaways.length
          } Gewinnspiel(e) ${
            action === "activate" ? "aktivieren" : "beenden"
          } möchten?`
        : `Are you sure you want to ${action} ${selectedGiveaways.length} giveaway(s)?`;

    confirm({
      title: actionConfig.title,
      icon: (
        <span
          style={{
            color: action === "activate" ? "#52c41a" : "#fa8c16",
            fontSize: "22px",
          }}
        >
          {actionConfig.icon}
        </span>
      ),
      content: (
        <div>
          <p>
            {replaceTemplate(confirmText, {
              count: selectedGiveaways.length,
            })}
          </p>

          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              {language === "de"
                ? "Betroffene Gewinnspiele:"
                : "Affected giveaways:"}
            </p>
            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded p-3">
              {selectedGiveaways.map((giveaway) => (
                <div
                  key={giveaway._id}
                  className="flex items-center gap-2 py-1"
                >
                  <span className="text-gray-500 text-sm">•</span>
                  <span className="text-sm">{giveaway.title}</span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      giveaway.status === "upcoming"
                        ? "bg-blue-100 text-blue-800"
                        : giveaway.status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {getStatusText(giveaway.status, language)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-3 text-blue-600 text-sm">{actionConfig.info}</p>
        </div>
      ),
      okText: actionConfig.okText,
      okType: "primary",
      cancelText: language === "de" ? "Abbrechen" : "Cancel",
      onOk: onConfirm,
      width: 600,
    });
  };

  const showDrawWinnersConfirm = (giveaway, onConfirm) => {
    confirm({
      title: content.drawModal.title,
      icon: <span style={{ color: "#722ed1", fontSize: "22px" }}>🎉</span>,
      content: (
        <div>
          <p>{content.drawModal.content}</p>

          <div className="mt-3 p-3 bg-purple-50 rounded">
            <strong>{giveaway?.title}</strong>
            {giveaway?.prizes && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">
                  {content.drawModal.prizes}
                </p>
                {giveaway.prizes.map((prize, index) => (
                  <div key={index} className="text-sm text-gray-600">
                    • {prize.title}: {prize.description} ({prize.value})
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="mt-3 text-red-600 text-sm">
            {content.drawModal.warning}
          </p>
          <p className="text-purple-600 text-sm">{content.drawModal.info}</p>
        </div>
      ),
      okText: content.drawModal.okText,
      okType: "primary",
      cancelText: content.drawModal.cancelText,
      onOk: onConfirm,
      width: 550,
    });
  };

  // ===== FILTER HANDLING =====
  const handleStatusFilter = (status) => {
    setFilterStatus(status);
    if (onFilterChange) {
      onFilterChange({ status });
    }
  };

  const handleSearch = (searchTerm) => {
    if (onFilterChange) {
      onFilterChange({ search: searchTerm });
    }
  };

  // ===== GIVEAWAY CRUD OPERATIONS =====
  const handleCreateNew = () => {
    setEditingGiveaway(null);
    setIsCreateOpen(true);
  };

  const handleEdit = (giveaway) => {
    setEditingGiveaway(giveaway);
    setIsCreateOpen(true);
  };
  const handleSave = async (savedData, isEdit) => {
    setActionLoading(true);
    try {
      console.log("📝 TAB handleSave called:", {
        isEdit,
        hasData: !!savedData,
        timestamp: new Date().toISOString(),
      });

      if (isEdit && editingGiveaway) {
        setGiveaways((prev) =>
          prev.map((g) => (g._id === editingGiveaway._id ? savedData : g))
        );
        message.success(content.success.giveawayUpdated);
      } else {
        setGiveaways((prev) => [savedData, ...prev]);
        message.success(content.success.giveawayCreated);
      }

      setIsCreateOpen(false);

      // ✅ Sichere onRefresh Behandlung
      if (onRefresh && typeof onRefresh === "function") {
        console.log("🔄 Calling onRefresh after save...");
        try {
          const refreshResult = onRefresh();
          // Nur .catch() aufrufen wenn es ein Promise ist
          if (refreshResult && typeof refreshResult.catch === "function") {
            refreshResult.catch((error) =>
              console.warn("Refresh failed:", error)
            );
          }
        } catch (error) {
          console.warn("onRefresh threw error:", error);
        }
      }
    } catch (error) {
      console.error("Fehler beim Speichern (UI Update):", error);
      const errorMessage = replaceTemplate(content.error.savingError, {
        error: error.message || error,
      });
      message.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ DELETE MIT ANTD MODAL
  const handleDelete = async (giveawayId) => {
    const giveaway = giveaways.find((g) => g._id === giveawayId);

    showDeleteConfirm(giveaway, async () => {
      setActionLoading(true);
      try {
        if (deleteContest) {
          const result = await deleteContest(giveawayId);
          console.log("📨 Delete result:", result);

          if (result?.success) {
            const mode = result?.mode || "archived"; // Fallback: archiviert
            if (mode === "hard_delete") {
              message.success(
                content?.success?.giveawayDeleted || "Gewinnspiel gelöscht."
              );
            } else {
              message.success(
                content?.success?.giveawayArchived || "Gewinnspiel archiviert."
              );
            }

            // Entferne das Item direkt aus der Liste (einfachster Weg)
            setGiveaways((prev) => prev.filter((g) => g._id !== giveawayId));
          } else {
            throw new Error(result?.message || content.error.deleteFailed);
          }
        } else {
          throw new Error(
            content.error.serviceNotAvailable + " - deleteContest"
          );
        }
      } catch (error) {
        console.error("Fehler beim Löschen:", error);
        const errorMessage = replaceTemplate(content.error.deletingError, {
          error: error.message || error,
        });
        message.error(errorMessage);
      } finally {
        setActionLoading(false);
      }
    });
  };

  // ===== GIVEAWAY ACTIONS =====
  const handleViewStats = async (giveawayId) => {
    const giveaway = giveaways.find((g) => g._id === giveawayId);

    setStatsModal({
      open: true,
      giveaway: giveaway,
      stats: null,
      loading: true,
    });

    try {
      if (getContestStats) {
        const stats = await getContestStats(giveawayId);

        setStatsModal((prev) => ({
          ...prev,
          stats: stats,
          loading: false,
        }));
      } else {
        // Fallback Mock Data für Demo
        const mockStats = {
          participants: Math.floor(Math.random() * 500) + 50,
          validEntries: Math.floor(Math.random() * 450) + 40,
          views: Math.floor(Math.random() * 1000) + 100,
          uniqueEmails: Math.floor(Math.random() * 400) + 35,
          returningParticipants: Math.floor(Math.random() * 50) + 5,
          avgRating: (Math.random() * 2 + 3).toFixed(1),
          avgPerDay: Math.floor(Math.random() * 20) + 5,
          abandonmentRate: Math.floor(Math.random() * 30) + 5,
          firstEntry: new Date(
            Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000
          ),
          lastEntry: new Date(),
          topFeedback: [
            {
              comment: "Tolles Gewinnspiel! Sehr faire Preise.",
              rating: 5,
              createdAt: new Date(),
            },
            {
              comment: "Einfache Teilnahme, gerne wieder!",
              rating: 4,
              createdAt: new Date(),
            },
            {
              comment: "Super organisiert, danke!",
              rating: 5,
              createdAt: new Date(),
            },
          ],
        };

        setTimeout(() => {
          setStatsModal((prev) => ({
            ...prev,
            stats: mockStats,
            loading: false,
          }));
        }, 1000);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Stats:", error);
      setStatsModal((prev) => ({
        ...prev,
        loading: false,
      }));
      message.error(content.error.statsError);
    }
  };

  const handleCloseStatsModal = () => {
    setStatsModal({
      open: false,
      giveaway: null,
      stats: null,
      loading: false,
    });
  };

  // ✅ DRAW WINNERS MIT ANTD MODAL
  const handleDrawWinners = async (giveawayId) => {
    const giveaway = giveaways.find((g) => g._id === giveawayId);

    showDrawWinnersConfirm(giveaway, async () => {
      try {
        setActionLoading(true);
        await onDrawWinners(giveawayId);
      } catch (error) {
        console.error("Fehler beim Auslosen:", error);
        message.error(content.error.drawError);
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handleViewWinners = (giveaway) => {
    if (giveaway.status === "drawn") {
      setActiveTab("winners");
    } else {
      message.info(content.warning.noDrawnWinners);
    }
  };

  // ===== BULK OPERATIONS MIT ANTD MODAL =====
  const handleBulkAction = async (action) => {
    let selectedGiveaways = [];
    let actionText = "";
    let newStatus = "";

    switch (action) {
      case "activate":
        // ✅ ALLE upcoming erlauben - Backend prüft Konflikte
        selectedGiveaways = giveaways.filter((g) => g.status === "upcoming");

        if (selectedGiveaways.length === 0) {
          message.warning(content.warning.noUpcomingToActivate);
          return;
        }

        actionText = getBulkActionText(action, language)?.action || "aktiviert";
        newStatus = "active";
        break;

      case "end":
        selectedGiveaways = giveaways.filter((g) => g.status === "active");
        actionText = getBulkActionText(action, language)?.action || "beendet";
        newStatus = "ended";
        break;

      default:
        message.warning(content.error.unknownAction);
        return;
    }

    if (selectedGiveaways.length === 0) {
      const warningKey =
        action === "activate" ? "noUpcomingToActivate" : "noActiveToEnd";
      message.warning(content.warning[warningKey]);
      return;
    }

    showBulkActionConfirm(action, selectedGiveaways, async () => {
      setActionLoading(true);

      try {
        if (updateContest && typeof updateContest === "function") {
          let successful = 0;
          let failed = 0;
          const errors = [];

          for (const giveaway of selectedGiveaways) {
            try {
              const giveawayId = giveaway._id || giveaway.id;

              const result = await updateContest(giveawayId, {
                status: newStatus,
              });

              let isSuccess = false;
              if (result && typeof result === "object") {
                if (result.success === true) {
                  isSuccess = true;
                } else if (result.status === "success") {
                  isSuccess = true;
                } else if (result._id && result.status === newStatus) {
                  isSuccess = true;
                } else if (result.ok === true) {
                  isSuccess = true;
                }
              } else if (result) {
                isSuccess = true;
              }

              if (isSuccess) {
                successful++;
              } else {
                failed++;
                const errorMsg =
                  result?.message || result?.error || "Unbekannter Fehler";
                errors.push(`${giveaway.title}: ${errorMsg}`);
              }
            } catch (error) {
              console.error(`❌ Exception updating ${giveaway.title}:`, error);
              failed++;

              // ✅ Spezielle Fehlermeldung für "bereits aktiv"
              if (error.response?.data?.error === "CONTEST_ALREADY_ACTIVE") {
                const conflictTitle =
                  error.response.data.conflictingContest?.title || "Unbekannt";
                errors.push(
                  `${giveaway.title}: ${
                    language === "de"
                      ? `Konflikt mit "${conflictTitle}" (bereits aktiv für dieses Formular)`
                      : `Conflict with "${conflictTitle}" (already active for this form)`
                  }`
                );
              } else {
                errors.push(`${giveaway.title}: ${error.message || error}`);
              }
            }
          }

          // State Update
          if (successful > 0) {
            setGiveaways((prev) =>
              prev.map((giveaway) => {
                const wasSelected = selectedGiveaways.some(
                  (selected) =>
                    (selected._id || selected.id) ===
                    (giveaway._id || giveaway.id)
                );
                if (wasSelected) {
                  return { ...giveaway, status: newStatus };
                }
                return giveaway;
              })
            );
          }

          // User Feedback
          if (successful > 0 && failed === 0) {
            const successKey =
              action === "activate" ? "bulkActivated" : "bulkEnded";
            const successMessage = replaceTemplate(
              content.success[successKey],
              {
                count: successful,
              }
            );
            message.success(successMessage);
          } else if (successful > 0 && failed > 0) {
            const successKey =
              action === "activate"
                ? "bulkActivatedWithErrors"
                : "bulkEndedWithErrors";
            const successMessage = replaceTemplate(
              content.success[successKey],
              {
                successful,
                failed,
              }
            );
            message.success(successMessage);
          }

          // ✅ Verbesserte Fehler-Ausgabe mit Details
          if (failed > 0) {
            console.error("❌ Bulk update errors:", errors);

            message.error({
              content: (
                <div>
                  <div className="font-medium mb-2">
                    {replaceTemplate(content.error.bulkUpdateFailed, {
                      count: failed,
                      action: actionText,
                    })}
                  </div>
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    {errors.map((err, idx) => (
                      <div key={idx} className="text-sm">
                        • {err}
                      </div>
                    ))}
                  </div>
                </div>
              ),
              duration: 8,
            });
          }
        } else {
          // Fallback ohne API
          setGiveaways((prev) =>
            prev.map((giveaway) => {
              const shouldUpdate = selectedGiveaways.some(
                (selected) =>
                  (selected._id || selected.id) ===
                  (giveaway._id || giveaway.id)
              );
              return shouldUpdate
                ? { ...giveaway, status: newStatus }
                : giveaway;
            })
          );

          const infoMessage = replaceTemplate(content.info.localUpdate, {
            count: selectedGiveaways.length,
            action: actionText,
          });
          message.success(infoMessage);
        }

        if (onRefresh && typeof onRefresh === "function") {
          await onRefresh();
        }
      } catch (error) {
        console.error("❌ Critical error in handleBulkAction:", error);
        const errorMessage = replaceTemplate(content.error.criticalError, {
          error: error.message || error,
        });
        message.error(errorMessage);
      } finally {
        setActionLoading(false);
      }
    });
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 space-y-4">
          {/* Title Section */}
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {content.title}
            </h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
              {content.subtitle}
            </p>
          </div>

          {/* Actions - Mobile Stack */}
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              onClick={onRefresh}
              disabled={loading || actionLoading || serviceLoading}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
            >
              {loading || serviceLoading ? (
                <Spin size="small" />
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              )}
              <span className="sm:hidden">{content.refresh}</span>
              <span className="hidden sm:inline">{content.refresh}</span>
            </button>

            <button
              onClick={handleCreateNew}
              disabled={loading || actionLoading || serviceLoading}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 sm:py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 font-medium"
            >
              <span className="text-lg sm:text-base">+</span>
              <span>{content.createNew}</span>
            </button>
          </div>
        </div>

        {/* Stats Modal */}
        <StatsModal
          open={statsModal.open}
          onClose={handleCloseStatsModal}
          stats={statsModal.stats}
          giveaway={statsModal.giveaway}
          loading={statsModal.loading}
          language={language}
        />

        {/* Filter Section */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Status Filter Buttons */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleStatusFilter("")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  !filterStatus
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {replaceTemplate(content.filterAll, {
                  count: giveawayCounts.all,
                })}
              </button>
              <button
                onClick={() => handleStatusFilter("upcoming")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "upcoming"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {replaceTemplate(content.filterUpcoming, {
                  count: giveawayCounts.upcoming,
                })}
              </button>
              <button
                onClick={() => handleStatusFilter("active")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "active"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {replaceTemplate(content.filterActive, {
                  count: giveawayCounts.active,
                })}
                {giveawayCounts.active > 0 && (
                  <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                    {content.badgeLive}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleStatusFilter("ended")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "ended"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {replaceTemplate(content.filterEnded, {
                  count: giveawayCounts.ended,
                })}
                {giveawayCounts.ended > 0 && (
                  <span className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
                    {content.badgeReady}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleStatusFilter("drawn")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "drawn"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {replaceTemplate(content.filterDrawn, {
                  count: giveawayCounts.drawn,
                })}
              </button>
            </div>

            {/* ✅ BULK ACTIONS MIT ANTD MODALS */}
            {(giveawayCounts.upcoming > 0 ||
              giveawayCounts.active > 0 ||
              giveawayCounts.ended > 0) && (
              <div className="flex flex-col sm:flex-row gap-2">
                {giveawayCounts.upcoming > 0 && (
                  <button
                    onClick={() => handleBulkAction("activate")}
                    disabled={
                      loading ||
                      actionLoading ||
                      serviceLoading ||
                      !updateContest
                    }
                    className="w-full sm:w-auto px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                    title={replaceTemplate(content.tooltipActivate, {
                      count: giveawayCounts.upcoming,
                    })}
                  >
                    {actionLoading || serviceLoading ? (
                      <Spin size="small" />
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M19 10a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                    <span className="hidden lg:inline">
                      {content.bulkActivateAll}
                    </span>
                    <span className="lg:hidden">
                      {replaceTemplate(content.bulkActivateShort, {
                        count: giveawayCounts.upcoming,
                      })}
                    </span>
                  </button>
                )}

                {giveawayCounts.active > 0 && (
                  <button
                    onClick={() => handleBulkAction("end")}
                    disabled={
                      loading ||
                      actionLoading ||
                      serviceLoading ||
                      !updateContest
                    }
                    className="w-full sm:w-auto px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                    title={replaceTemplate(content.tooltipEnd, {
                      count: giveawayCounts.active,
                    })}
                  >
                    {actionLoading || serviceLoading ? (
                      <Spin size="small" />
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 10h6v4H9z"
                        />
                      </svg>
                    )}
                    <span className="hidden lg:inline">
                      {content.bulkEndAll}
                    </span>
                    <span className="lg:hidden">
                      {replaceTemplate(content.bulkEndShort, {
                        count: giveawayCounts.active,
                      })}
                    </span>
                  </button>
                )}

                {giveawayCounts.ended > 0 && (
                  <button
                    onClick={() => {
                      const infoMessage = replaceTemplate(
                        content.info.readyForDraw,
                        {
                          count: giveawayCounts.ended,
                        }
                      );
                      message.info(infoMessage);
                    }}
                    disabled={loading || actionLoading || serviceLoading}
                    className="w-full sm:w-auto px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                    title={replaceTemplate(content.tooltipDraw, {
                      count: giveawayCounts.ended,
                    })}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                      />
                    </svg>
                    <span className="hidden lg:inline">
                      {content.bulkDrawReady}
                    </span>
                    <span className="lg:hidden">
                      {replaceTemplate(content.bulkDrawShort, {
                        count: giveawayCounts.ended,
                      })}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading || serviceLoading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <Spin size="large" />
              <p className="mt-4 text-gray-600">{content.loadingGiveaways}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Service Error State */}
            {serviceError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-800">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-medium">{content.serviceError}</span>
                  <span>{serviceError}</span>
                </div>
              </div>
            )}

            {/* Giveaways Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {giveaways.map((giveaway) => (
                <GiveawayCard
                  key={giveaway._id}
                  giveaway={giveaway}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewWinners={() => handleViewWinners(giveaway)}
                  onViewStats={() => handleViewStats(giveaway._id)}
                  onDrawWinners={() => handleDrawWinners(giveaway._id)}
                  loading={actionLoading || serviceLoading}
                  language={language}
                />
              ))}
            </div>

            {/* Empty State */}
            {giveaways.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-4">🎁</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {content.noGiveaways}
                </h3>
                <p className="text-gray-500 mb-4">
                  {filterStatus
                    ? replaceTemplate(content.noGiveawaysFiltered, {
                        status: getStatusText(filterStatus, language),
                      })
                    : content.noGiveawaysFirst}
                </p>

                <div className="flex gap-3 justify-center">
                  {filterStatus && (
                    <button
                      onClick={() => handleStatusFilter("")}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      {content.showAllGiveaways}
                    </button>
                  )}
                  <button
                    onClick={handleCreateNew}
                    disabled={serviceLoading || !createContest}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                  >
                    + {content.createFirst}
                  </button>
                </div>
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onPageChange(pagination.currentPage - 1)}
                    disabled={
                      pagination.currentPage === 1 || loading || serviceLoading
                    }
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-300"
                  >
                    ← {content.back}
                  </button>

                  <div className="flex items-center gap-2">
                    {[...Array(Math.min(5, pagination.totalPages))].map(
                      (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            disabled={loading || serviceLoading}
                            className={`px-3 py-2 rounded-lg ${
                              pagination.currentPage === page
                                ? "bg-orange-500 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      }
                    )}
                  </div>

                  <span className="text-sm text-gray-600">
                    {replaceTemplate(content.page, {
                      current: pagination.currentPage,
                      total: pagination.totalPages,
                      items: pagination.totalItems,
                    })}
                  </span>

                  <button
                    onClick={() => onPageChange(pagination.currentPage + 1)}
                    disabled={
                      pagination.currentPage === pagination.totalPages ||
                      loading ||
                      serviceLoading
                    }
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-300"
                  >
                    {content.next} →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Loading Overlay */}
        {(actionLoading || serviceLoading) && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3">
              <Spin size="large" />
              <span className="text-lg">
                {serviceLoading
                  ? content.loadingService
                  : content.loadingAction}
              </span>
            </div>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <GiveawayForm
          open={isCreateOpen}
          onCancel={() => setIsCreateOpen(false)}
          onSave={handleSave}
          editingGiveaway={editingGiveaway}
          loading={actionLoading || serviceLoading}
          language={language}
          // ✅ Übergebe den importierten Service an das Form
          contestService={{
            updateContest,
            createContest,
            deleteContest,
            getContestStats,
            loading: serviceLoading,
            error: serviceError,
          }}
        />
      </div>
    </div>
  );
}
